import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { currencySymbol } from '../constants/currency';
import api, { apiErrorMessage, type Payment, type Student, type Course } from '../services/api';
import {
  IconGroup, IconMoney, IconChart, IconReceipt,
  IconCreditCard, IconCash, IconComment, IconHomework,
} from '../components/Icons';

const PAYMENT_TYPE_LABELS: Record<string, string> = {
  monthly: 'Месяц',
  multi_month: 'Несколько месяцев',
  single_lesson: 'Разовое занятие',
  partial: 'Частичная',
  full: 'Произвольная',
};

export default function PaymentsPage() {
  const navigate = useNavigate();
  const { permissions, user } = useAuth();
  const curSymbol = currencySymbol(user?.currency);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);

  // ── Payment modal state ─────────────────────────────────────────────
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_date: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })(),
    payment_type: 'monthly' as string,
    payment_method: 'cash' as string,
    student_id: '',
    course_id: '',
    comment: '',
    // Сколько занятий входит в абонемент ученика (по умолчанию 8).
    lessons_per_month: '8',
  });

  // ── Receipt modal state ──────────────────────────────────────────────
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [pData, sData, cData] = await Promise.all([
        api.getPayments(),
        api.getStudents(),
        api.getCourses(),
      ]);
      setPayments(pData.payments);
      setStudents(sData.students);
      setCourses(cData.courses);
    } catch (e) {
      console.error('Failed to load payments:', e);
    } finally {
      setLoading(false);
    }
  };

  const getStudentName = (id: string) => {
    const s = students.find(st => st.id === id);
    return s ? `${s.first_name} ${s.last_name}` : id;
  };

  const getCourseTitle = (id: string) => {
    const c = courses.find(co => co.id === id);
    return c?.title || id;
  };

  // ── Payment modal helpers ─────────────────────────────────────────────

  const openPaymentModal = () => {
    const firstStudent = students[0];
    const firstCourse = courses[0];
    const monthlyPrice = parseFloat(firstCourse?.monthly_price || firstCourse?.price || '0') || 0;

    setPaymentForm({
      amount: String(monthlyPrice),
      payment_date: new Date().toISOString().split('T')[0],
      payment_type: 'monthly',
      payment_method: 'cash',
      student_id: firstStudent?.id || '',
      course_id: firstCourse?.id || '',
      comment: '',
      lessons_per_month: '8',
    });
    setShowPaymentModal(true);
  };

  const handlePaymentTypeChange = (type: string) => {
    const selectedCourse = courses.find(c => c.id === paymentForm.course_id);
    const monthlyPrice = parseFloat(selectedCourse?.monthly_price || selectedCourse?.price || '0') || 0;
    const lessonPrice = parseFloat(selectedCourse?.lesson_price || '0') || 0;

    let amount = 0;

    switch (type) {
      case 'monthly':
        amount = monthlyPrice;
        break;
      case 'multi_month':
        amount = monthlyPrice * 3;
        break;
      case 'single_lesson':
        amount = lessonPrice || Math.round(monthlyPrice / 4);
        break;
      case 'partial':
        amount = Math.round(monthlyPrice / 2);
        break;
      case 'full':
        amount = 0;
        break;
    }

    setPaymentForm(f => ({
      ...f,
      payment_type: type,
      amount: amount ? String(amount) : '',
    }));
  };

  const handleSavePayment = async () => {
    if (savingPayment) return;
    setSavingPayment(true);
    try {
      const lessonsPerMonth = parseInt(paymentForm.lessons_per_month, 10);
      await api.createPayment({
        student_id: paymentForm.student_id,
        course_id: paymentForm.course_id,
        amount: parseFloat(paymentForm.amount) || 0,
        payment_date: paymentForm.payment_date,
        payment_type: paymentForm.payment_type,
        payment_method: paymentForm.payment_method,
        comment: paymentForm.comment,
        lessons_per_month: Number.isFinite(lessonsPerMonth) && lessonsPerMonth > 0 ? lessonsPerMonth : undefined,
      });
      setShowPaymentModal(false);
      await loadData();
    } catch (e) {
      console.error('Failed to save payment:', e);
      alert(apiErrorMessage(e, 'Ошибка при сохранении платежа'));
    } finally {
      setSavingPayment(false);
    }
  };

  const totalAmount = payments.reduce((sum, p) => sum + parseFloat(p.amount || '0'), 0);
  const paymentsByStudent = new Map<string, number>();
  payments.forEach(p => {
    const sid = p.student_id;
    paymentsByStudent.set(sid, (paymentsByStudent.get(sid) || 0) + 1);
  });

  if (loading) {
    return <div className="p-4 text-center text-sm text-[var(--tg-theme-hint-color)]">Загрузка...</div>;
  }

  return (
    <div className="p-4 space-y-4 animate-fade-in">
      {/* ── Payment modal (add new) ─────────────────────────────────────── */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-2 animate-fade-in"
          onClick={() => setShowPaymentModal(false)}>
          <div className="bg-[var(--tg-theme-bg-color)] rounded-3xl w-full max-w-md shadow-2xl animate-slide-up max-h-[95vh] flex flex-col"
            onClick={e => e.stopPropagation()}>

            {/* Sticky header */}
            <div className="flex items-center justify-between p-5 pb-0 shrink-0">
              <h3 className="text-lg font-bold text-[var(--tg-theme-text-color)]">➕ Добавить платеж</h3>
              <button onClick={() => setShowPaymentModal(false)}
                className="w-8 h-8 rounded-full bg-[var(--tg-theme-secondary-bg-color)] flex items-center justify-center text-sm hover:opacity-70 transition-opacity">✕</button>
            </div>

            {/* Scrollable form */}
            <div className="overflow-y-auto p-5 pb-8">
              {/* Student selector */}
              <div className="mb-4">
                <label className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-1.5 block">Ученик</label>
                <select value={paymentForm.student_id}
                  onChange={e => setPaymentForm(f => ({ ...f, student_id: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2 focus:ring-[var(--tg-theme-button-color)]/30">
                  {students.map(s => (
                    <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>
                  ))}
                </select>
              </div>

              {/* Course selector */}
              <div className="mb-4">
                <label className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-1.5 block">Курс</label>
                <select value={paymentForm.course_id}
                  onChange={e => {
                    const c = courses.find(co => co.id === e.target.value);
                    const mp = parseFloat(c?.monthly_price || c?.price || '0') || 0;
                    setPaymentForm(f => ({
                      ...f,
                      course_id: e.target.value,
                      amount: f.payment_type === 'monthly' ? String(mp) : f.amount,
                    }));
                  }}
                  className="w-full px-4 py-2.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2 focus:ring-[var(--tg-theme-button-color)]/30">
                  {courses.map(c => (
                    <option key={c.id} value={c.id}>{c.title}</option>
                  ))}
                </select>
              </div>

              {/* Payment type */}
              <div className="mb-4">
                <label className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-1.5 block">Тип платежа</label>
                <div className="grid grid-cols-2 gap-1.5">
                  {[
                    { value: 'monthly', label: '📆 Месяц' },
                    { value: 'multi_month', label: '📚 Несколько месяцев' },
                    { value: 'single_lesson', label: '📖 Разовое занятие' },
                    { value: 'partial', label: '✂️ Частичная' },
                    { value: 'full', label: '📝 Произвольная' },
                  ].map(opt => (
                    <button key={opt.value}
                      onClick={() => handlePaymentTypeChange(opt.value)}
                      className={`px-3 py-2 rounded-xl text-xs font-medium border transition-all ${
                        paymentForm.payment_type === opt.value
                          ? 'border-[var(--tg-theme-button-color)] bg-[var(--tg-theme-button-color)]/10 text-[var(--tg-theme-button-color)]'
                          : 'border-[var(--tg-theme-section-separator-color)] bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)]'
                      }`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Payment method */}
              <div className="mb-4">
                <label className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-1.5 block">Способ оплаты</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPaymentForm(f => ({ ...f, payment_method: 'cash' }))}
                    className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-medium border transition-all ${
                      paymentForm.payment_method === 'cash'
                        ? 'border-green-400 bg-green-50 text-green-700'
                        : 'border-[var(--tg-theme-section-separator-color)] bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)]'
                    }`}>
                    <IconCash className="w-4 h-4 inline" /> Наличные
                  </button>
                  <button
                    onClick={() => setPaymentForm(f => ({ ...f, payment_method: 'card' }))}
                    className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-medium border transition-all ${
                      paymentForm.payment_method === 'card'
                        ? 'border-blue-400 bg-blue-50 text-blue-700'
                        : 'border-[var(--tg-theme-section-separator-color)] bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)]'
                    }`}>
                    <IconCreditCard className="w-4 h-4 inline" /> Карта
                  </button>
                </div>
              </div>

              {/* Amount */}
              <div className="mb-4">
                <label className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-1.5 block">Сумма ({curSymbol})</label>
                <input type="number" value={paymentForm.amount}
                  onChange={e => setPaymentForm(f => ({ ...f, amount: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-lg font-bold outline-none focus:ring-2 focus:ring-[var(--tg-theme-button-color)]/30"
                  placeholder="0" />
              </div>

              {/* Lessons per month (абонемент) */}
              <div className="mb-4">
                <label className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-1.5 block">
                  Сколько занятий входит в месячный абонемент?
                </label>
                <div className="flex gap-2 mb-2">
                  {[8, 12, 16].map(n => (
                    <button key={n} type="button"
                      onClick={() => setPaymentForm(f => ({ ...f, lessons_per_month: String(n) }))}
                      className={`flex-1 px-3 py-2 rounded-xl text-sm font-semibold border transition-all ${
                        paymentForm.lessons_per_month === String(n)
                          ? 'border-[var(--tg-theme-button-color)] bg-[var(--tg-theme-button-color)]/10 text-[var(--tg-theme-button-color)]'
                          : 'border-[var(--tg-theme-section-separator-color)] bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)]'
                      }`}>
                      {n}
                    </button>
                  ))}
                </div>
                <input type="number" min={1} step={1} value={paymentForm.lessons_per_month}
                  onChange={e => setPaymentForm(f => ({ ...f, lessons_per_month: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2 focus:ring-[var(--tg-theme-button-color)]/30"
                  placeholder="Например: 8" />
                <p className="text-[11px] text-[var(--tg-theme-hint-color)] mt-1">
                  Отсчёт посещений начнётся с первого занятия. Оставьте поле пустым, если абонемент не нужен.
                </p>
              </div>

              {/* Date */}
              <div className="mb-4">
                <label className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-1.5 block">Дата платежа</label>
                <input type="date" value={paymentForm.payment_date}
                  onChange={e => setPaymentForm(f => ({ ...f, payment_date: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2 focus:ring-[var(--tg-theme-button-color)]/30" />
              </div>

              {/* Comment */}
              <div className="mb-5">
                <label className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-1.5 block">Комментарий</label>
                <input type="text" value={paymentForm.comment}
                  onChange={e => setPaymentForm(f => ({ ...f, comment: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2 focus:ring-[var(--tg-theme-button-color)]/30"
                  placeholder="Например: оплата за июль" />
              </div>

              <button onClick={handleSavePayment} disabled={savingPayment || !paymentForm.amount || !paymentForm.student_id}
                className="w-full tg-button text-base disabled:opacity-50">
                {savingPayment ? 'Сохранение...' : '✓ Сохранить платеж'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Receipt modal (view details) ────────────────────────────────── */}
      {showReceiptModal && selectedPayment && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-2 animate-fade-in"
          onClick={() => setShowReceiptModal(false)}>
          <div className="bg-[var(--tg-theme-bg-color)] rounded-3xl w-full max-w-sm shadow-2xl animate-slide-up max-h-[95vh] flex flex-col overflow-hidden"
            onClick={e => e.stopPropagation()}>

            {/* Gradient header */}
            <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 p-6 text-center shrink-0">
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-2">
                <IconReceipt className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-white font-bold text-lg">Чек оплаты</h3>
              <p className="text-emerald-100 text-xs mt-0.5">
                №{selectedPayment.id.slice(0, 8)}…
              </p>
            </div>

            {/* Scrollable content */}
            <div className="overflow-y-auto flex-1 p-5">
              {/* Student name */}
              <div className="flex items-center gap-3 pb-4 mb-4 border-b border-[var(--tg-theme-section-separator-color)]">
                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                  <IconGroup className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-xs text-[var(--tg-theme-hint-color)]">Ученик</p>
                  <p className="text-base font-semibold text-[var(--tg-theme-text-color)]">
                    {getStudentName(selectedPayment.student_id)}
                  </p>
                </div>
              </div>

              {/* Details */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-[var(--tg-theme-hint-color)]">Курс</span>
                  <span className="text-sm font-medium text-right max-w-[60%]">{getCourseTitle(selectedPayment.course_id)}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-xs text-[var(--tg-theme-hint-color)]"><IconMoney className="w-3 h-3 inline" /> Сумма</span>
                  <span className="text-xl font-bold text-emerald-600">{parseFloat(selectedPayment.amount).toLocaleString()} {curSymbol}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-[var(--tg-theme-hint-color)]">Дата</span>
                  <span className="text-sm font-medium">{selectedPayment.payment_date || '—'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-[var(--tg-theme-hint-color)]">Тип</span>
                  <span className="text-sm">{PAYMENT_TYPE_LABELS[selectedPayment.payment_type] || selectedPayment.payment_type || '—'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-[var(--tg-theme-hint-color)]"><IconCreditCard className="w-3 h-3 inline" /> Способ</span>
                  <span className="text-sm">
                    {selectedPayment.payment_method === 'card' ? <><IconCreditCard className="w-3 h-3 inline" /> Карта</> : <><IconCash className="w-3 h-3 inline" /> Наличные</>}
                  </span>
                </div>
                {selectedPayment.comment && (
                  <div className="pt-3 border-t border-[var(--tg-theme-section-separator-color)]">
                    <span className="text-xs text-[var(--tg-theme-hint-color)] block mb-1.5"><IconComment className="w-3 h-3 inline" /> Комментарий</span>
                    <p className="text-sm text-[var(--tg-theme-text-color)] bg-[var(--tg-theme-secondary-bg-color)] rounded-xl px-3 py-2">
                      {selectedPayment.comment}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-2 p-5 pt-3 shrink-0 border-t border-[var(--tg-theme-section-separator-color)]">
              <button
                onClick={() => { setShowReceiptModal(false); navigate(`/student/${selectedPayment.student_id}`); }}
                className="flex-1 py-2.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm font-medium hover:opacity-80 transition-opacity active:scale-[0.98]">
                <IconGroup className="w-4 h-4 inline" /> Профиль ученика
              </button>
              <button
                onClick={() => setShowReceiptModal(false)}
                className="flex-1 py-2.5 rounded-xl bg-[var(--tg-theme-button-color)] text-[var(--tg-theme-button-text-color)] text-sm font-medium hover:opacity-90 transition-opacity active:scale-[0.98]">
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="text-sm text-[var(--tg-theme-button-color)] flex items-center gap-1">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Назад
        </button>
        <h2 className="text-base font-semibold">Журнал платежей</h2>
        {permissions.canAddPayments && (
          <button onClick={openPaymentModal} className="tg-button text-sm py-1.5 px-3 flex items-center gap-1">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Добавить
          </button>
        )}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-green-50 rounded-2xl p-3 text-center border border-green-100">
          <IconMoney className="w-5 h-5 text-green-500" />
          <p className="text-lg font-bold text-green-600">{payments.length}</p>
          <p className="text-[10px] text-green-500">Платежей</p>
        </div>
        <div className="bg-blue-50 rounded-2xl p-3 text-center border border-blue-100">
          <IconGroup className="w-5 h-5 text-blue-500" />
          <p className="text-lg font-bold text-blue-600">{paymentsByStudent.size}</p>
          <p className="text-[10px] text-blue-500">Учеников</p>
        </div>
        <div className="bg-amber-50 rounded-2xl p-3 text-center border border-amber-100">
          <IconChart className="w-5 h-5 text-amber-500" />
          <p className="text-lg font-bold text-amber-600">{totalAmount.toLocaleString()}</p>
          <p className="text-[10px] text-amber-500">{curSymbol} всего</p>
        </div>
      </div>

      {/* Payments list — кликабельно → чек */}
      <div className="space-y-2">
        {payments.length === 0 ? (
          <div className="text-center py-8 text-sm text-[var(--tg-theme-hint-color)]">
            Нет записей о платежах
          </div>
        ) : (
          payments.map(p => {
            const typeLabel = PAYMENT_TYPE_LABELS[p.payment_type] || p.payment_type || '—';
            return (
              <button key={p.id}
                onClick={() => { setSelectedPayment(p); setShowReceiptModal(true); }}
                className="w-full tg-card text-left hover:shadow-md transition-all active:scale-[0.98]">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium block truncate">
                      {getStudentName(p.student_id)}
                    </span>
                    <span className="text-xs text-[var(--tg-theme-hint-color)]">
                      {getCourseTitle(p.course_id)} · {parseFloat(p.amount).toLocaleString()} {curSymbol}
                    </span>
                  </div>
                  <span className="ml-1 text-xs">{p.payment_method === 'card' ? <IconCreditCard className="w-4 h-4 text-blue-500" /> : <IconCash className="w-4 h-4 text-green-500" />}</span>
                  <span className="ml-1 px-2.5 py-1 rounded-full text-[10px] font-medium bg-green-50 text-green-700 border border-green-100">
                    {typeLabel}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs text-[var(--tg-theme-hint-color)] mb-2">
                  <span>{p.payment_date || '—'}</span>
                </div>
                {p.comment && (
                  <p className="text-xs text-[var(--tg-theme-hint-color)] bg-[var(--tg-theme-secondary-bg-color)] rounded-lg px-2.5 py-1.5">
                    <IconComment className="w-3 h-3 inline" /> {p.comment}
                  </p>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
